import LegalPage from "@/components/LegalPage";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import SupportRequestForm from "@/components/SupportRequestForm";

const PrivacyPage = () => (
  <LegalPage
    title="Privacy Policy"
    updated="June 18, 2026"
    intro={
      <p>
        This Privacy Policy describes how Vecurate ("we", "us", or "our") collects, uses, and shares
        information when you use vecurate.net and related services (the "Service"). Vecurate is built
        around anonymous reviews — protecting reviewer privacy is core to how the platform works.
      </p>
    }
  >
    <h2>1. Information We Collect</h2>

    <h3>Account information</h3>
    <p>
      When you create an account, we collect your email address, a hashed password (we never store
      passwords in plain text), and any optional profile fields you choose to provide.
    </p>

    <h3>Profile information</h3>
    <p>
      You may optionally provide additional details such as your school, major, or skills. Reviews
      themselves are published anonymously as "Anonymous Student" and are not attributed to your
      profile or email address.
    </p>

    <h3>Review and submission content</h3>
    <p>
      We collect the content you submit, including ratings, written reviews, tags, supervisor or
      preceptor names you choose to include, location, and any other information you enter into a
      submission form. Reviews are intended for public display.
    </p>

    <h3>Usage and diagnostic data</h3>
    <p>
      Through our error monitoring and performance provider (see Section 3), we receive limited
      diagnostic information when errors occur or performance traces are sampled, such as the page or
      route, basic browser and device type, and a truncated form of your IP address. We use this
      information to operate, secure, debug, and improve the Service.
    </p>

    <h3>Communications</h3>
    <p>
      If you contact us by email or through a support form, we keep a record of that correspondence so
      that we can respond and improve our support.
    </p>

    <h2>2. Cookies and Similar Technologies</h2>
    <p>
      We use cookies and similar technologies (such as local storage) to keep you signed in, remember
      preferences, secure the Service against abuse, and understand aggregate usage. You can control
      cookies through your browser settings; blocking some cookies may break sign-in or other features.
    </p>
    <p>We use cookies and similar technologies in the following broad categories:</p>
    <ul>
      <li><strong>Strictly necessary</strong> — authentication, session management, security, and core functionality.</li>
      <li><strong>Preferences</strong> — remembering settings such as theme or location filters.</li>
      <li><strong>Error and performance monitoring</strong> — limited diagnostic signals used to detect and fix issues (see Section 3).</li>
    </ul>

    <h2>3. Security and Anti-Abuse Tools</h2>
    <p>
      To protect Vecurate and its users from spam, fraud, and abuse, we use third-party security and
      reliability services, including:
    </p>
    <ul>
      <li>
        <strong>Cloudflare Turnstile</strong> — a privacy-focused CAPTCHA used on sign-in, sign-up,
        password reset, and review submission flows to verify that requests come from real users rather
        than automated bots. Turnstile may process limited browser and network signals to perform this
        check.
      </li>
      <li>
        <strong>Sentry</strong> — an error monitoring and performance service that helps us detect,
        diagnose, and fix bugs. Sentry receives error reports, performance traces, and session replays
        (the contents of password, email, and review fields, and other sensitive inputs are masked
        before transmission).
      </li>
      <li>
        <strong>Backend infrastructure</strong> — our hosting and database providers process data on
        our behalf solely to operate the Service.
      </li>
    </ul>

    <h2>4. How We Use Information</h2>
    <p>We use the information we collect to:</p>
    <ul>
      <li>Provide, operate, and maintain the Service;</li>
      <li>Authenticate users and protect accounts;</li>
      <li>Display anonymous reviews and aggregated ratings;</li>
      <li>Enforce our Terms of Service, Content Policy, and Community Guidelines;</li>
      <li>Detect, prevent, and respond to security incidents, fraud, and abuse;</li>
      <li>Communicate with you about the Service (for example, security notices or account-related messages);</li>
      <li>Improve and develop new features;</li>
      <li>Comply with legal obligations.</li>
    </ul>

    <h2>5. How We Share Information</h2>
    <p>We share information only in the following limited circumstances:</p>
    <ul>
      <li>
        <strong>Service providers.</strong> With vendors that process data on our behalf (such as
        hosting, database, email delivery, error monitoring, and anti-abuse providers) under
        confidentiality and data-protection obligations.
      </li>
      <li>
        <strong>Legal and safety.</strong> When we reasonably believe disclosure is necessary to comply
        with a valid legal request, enforce our Terms, protect the rights, property, or safety of
        Vecurate, our users, or the public, or respond to an emergency.
      </li>
      <li>
        <strong>Business transfers.</strong> In connection with a merger, acquisition, financing, or
        sale of assets, subject to a successor honoring this Privacy Policy or providing notice and
        choice.
      </li>
      <li>
        <strong>Aggregated or de-identified data.</strong> We may share information that has been
        aggregated or de-identified so that it cannot reasonably be used to identify you.
      </li>
    </ul>
    <p>
      <strong>We do not sell your personal information.</strong> We do not rent your personal
      information, and we do not share it with third parties for their own advertising or marketing
      purposes.
    </p>

    <h2>6. Public Content and Anonymity</h2>
    <p>
      Reviews, ratings, and comments you submit are intended to appear publicly on the Service.
      Reviews are attributed to "Anonymous Student" rather than your account. Please do not include
      information in a review that you do not want to be public, and avoid including details that could
      reasonably identify you, your patients, your coworkers, or other private individuals.
    </p>

    <h2>7. Data Retention</h2>
    <p>
      We retain information for as long as your account is active or as needed to provide the Service.
      Specifically:
    </p>
    <ul>
      <li>Account information is retained until you delete your account;</li>
      <li>Published reviews may be retained after account deletion in anonymized form so that the public review history remains intact;</li>
      <li>Security, error, and abuse-prevention logs are retained for a limited period proportionate to their purpose;</li>
      <li>We may retain information longer when required to comply with legal obligations, resolve disputes, or enforce our agreements.</li>
    </ul>

    <h2>8. Account Deletion</h2>
    <p>
      You can request deletion of your account at any time by emailing{" "}
      <a href="mailto:support@vecurate.net">support@vecurate.net</a>. When you delete your account, we
      remove or anonymize personal information associated with the account within a reasonable period,
      subject to backup retention cycles and any legal obligations. Anonymous reviews may remain
      publicly visible.
    </p>

    <h2>9. Your Rights</h2>
    <p>
      Depending on where you live, you may have rights to access, correct, delete, port, or restrict the
      processing of your personal information, and to object to certain processing. To exercise these
      rights, email <a href="mailto:support@vecurate.net">support@vecurate.net</a>. We will respond
      within the time required by applicable law. You also have the right to lodge a complaint with a
      data-protection authority.
    </p>

    <h2>10. Children</h2>
    <p>
      The Service is not directed to children under 16. We do not knowingly collect personal information
      from children under 16. If you believe a child has provided us with personal information, please
      contact <a href="mailto:support@vecurate.net">support@vecurate.net</a> so we can take appropriate
      action.
    </p>

    <h2>11. International Users</h2>
    <p>
      Vecurate is operated from the United States. If you access the Service from outside the United
      States, you understand that your information may be transferred to, stored, and processed in the
      United States and other countries where our service providers operate.
    </p>

    <h2>12. Changes to This Policy</h2>
    <p>
      We may update this Privacy Policy from time to time. We will post the updated version with a new
      "Last updated" date and, where appropriate, provide additional notice.
    </p>

    <h2>13. Contact</h2>
    <p>
      For privacy questions or requests, email{" "}
      <a href="mailto:support@vecurate.net">support@vecurate.net</a>.
    </p>

    <Dialog>
      <DialogTrigger asChild>
        <Button className="rounded-lg not-prose mt-2">Submit a privacy request</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Submit a privacy request</DialogTitle>
          <DialogDescription>
            Use this form for access, correction, or deletion requests. We'll verify your identity by email.
          </DialogDescription>
        </DialogHeader>
        <SupportRequestForm
          requestType="privacy"
          defaultSubject="Privacy request"
          submitLabel="Submit privacy request"
        />
      </DialogContent>
    </Dialog>

    <div className="callout">
      <strong>Note:</strong> This Privacy Policy is provided for informational purposes and should be
      reviewed by qualified privacy counsel before being relied upon in any specific jurisdiction.
    </div>
  </LegalPage>
);

export default PrivacyPage;
